import type { CustomImageData, IntendedDisplay } from '@iheartjane/dm-sdk';
import { Box, Image, effects } from '@iheartjane/reefer';
import { clsx } from 'clsx';
import type { CSSProperties } from 'react';
import { useMemo, useState } from 'react';

import { AdLayout } from './adLayout/adLayout';
import { AdProductCard } from './adProductCard/adProductCard';
import { useCustomImageAdAnalytics } from './useCustomImageAdAnalytics';
import type { WebCustomImageAdProps } from './webCustomImageAd.types';

import styles from './webCustomImageAd.module.css';

// TODO: copied from brands, setup a shared utils for constants for customImageAds
const SURFACE_DIMENSIONS: Record<
  `${IntendedDisplay}`,
  { height: number; width: number }
> = {
  desktop: { height: 197, width: 816 },
  // Adding for TS, component doesn't support Kiosk
  kiosk: { height: 317, width: 952 },
  mobile: { height: 251, width: 361 },
};

// The crop is based on how image is cropped in brands manager. We are replicating it here
const cropStyles = (image: CustomImageData): CSSProperties => {
  const surfaceDimensions = SURFACE_DIMENSIONS[image.intendedDisplay];

  return {
    '--ad-offset': `translate(${
      (image.positionX / surfaceDimensions.width) * 100
    }%, ${(image.positionY / surfaceDimensions.height) * 100}%)`,
    '--ad-scale': `${image.scaleNumber}`,
  };
};

/**
 * A custom image ad for the web surfaces — desktop and mobile. Purely
 * presentational: it renders the one creative it is handed and reports clicks
 * and swipes through `onEvent`. Choosing which creative to hand it belongs to
 * the host; reporting views belongs to `BannerCarousel`.
 *
 * Scope: the web menu apps — Bloom, Boost, and Marketplace. The Kiosk app
 * (`apps/kiosk`) does not render this component and is not a consumer to
 * design, test, or check compatibility against. Kiosk gets its own ad surface.
 * Do not treat `apps/kiosk/.browserslistrc` as a constraint here.
 *
 * The `kiosk` key in `SURFACE_DIMENSIONS` below is unrelated — it is a value of
 * the SDK's `IntendedDisplay` enum describing where a *creative* was authored,
 * not the Kiosk app.
 */
export const WebCustomImageAd = ({
  className,
  customImageData,
  onEvent,
  onImageClick,
  products,
  typography,
}: WebCustomImageAdProps) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const {
    brandBlockRef,
    productsRowRef,
    trackCreativeClick,
    trackProductClick,
  } = useCustomImageAdAnalytics(customImageData, onEvent);

  const imageOverlayRgb = useMemo(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    () =>
      customImageData?.overlayColor
        ? effects.hexToRgb(customImageData.overlayColor)
        : [],
    [customImageData?.overlayColor]
  );

  if (!customImageData) {
    return null;
  }

  const {
    headline: tagline,
    imageUrl,
    intendedDisplay,
    logoUrl: brandLogoUrl,
    overlayColor,
    textColor,
  } = customImageData;

  const brandName = products[0]?.brand ?? '';
  const taglineText = tagline || '';

  const typographyColor =
    textColor === '#FFFFFF' ? 'grays-white' : 'grays-black';

  const cropStyle = cropStyles(customImageData);

  const adImage = (
    <Image
      src={imageUrl}
      altText={`${brandName} ad`}
      onLoad={() => setImageLoaded(true)}
      objectFit="contain"
      responsive
      height="100%"
      width="100%"
    />
  );

  const creative = onImageClick ? (
    <button
      type="button"
      className={styles['creative']}
      data-surface={intendedDisplay}
      data-testid="custom-image-ad-creative"
      style={cropStyle}
      onClick={(event) => {
        onImageClick(brandName);
        trackCreativeClick(event);
      }}
    >
      {adImage}
    </button>
  ) : (
    <div
      className={styles['creative']}
      data-surface={intendedDisplay}
      data-testid="custom-image-ad-creative"
      style={cropStyle}
    >
      {adImage}
    </div>
  );

  return (
    <Box className={clsx(styles['root'], className)}>
      {/*
        `height` makes the ad fill the slide it is handed, so it is always
        contained by it. Load-bearing, not cosmetic: `.layout`, `.productsRow`
        and the cards are sized with `height: 100%`, and the desktop card's
        `aspect-ratio` derives its width from that height.
      */}
      <Box
        className={clsx({
          [styles['imageLoaded']]: imageLoaded,
          [styles['imageNotLoaded']]: !imageLoaded,
        })}
        width="100%"
        height="100%"
        position="relative"
        overflow="hidden"
        borderRadius="lg"
      >
        {creative}

        {overlayColor && (
          <Box
            position="absolute"
            top={0}
            left={0}
            width="100%"
            height="100%"
            zIndex={2}
            className={styles['noPointerEvents']}
            style={{
              backgroundColor: `rgba(${imageOverlayRgb[0]}, ${imageOverlayRgb[1]}, ${imageOverlayRgb[2]}, 0.2)`,
            }}
          />
        )}

        <AdLayout
          brandBlockRef={brandBlockRef}
          brandLogoUrl={brandLogoUrl}
          brandName={brandName}
          productsRowRef={productsRowRef}
          taglineText={taglineText}
          typography={typography}
          typographyColor={typographyColor}
        >
          {products.map((product, index) => (
            <AdProductCard
              key={product.product_id}
              onProductClick={() => trackProductClick(product, index)}
              product={product}
              typography={typography}
            />
          ))}
        </AdLayout>
      </Box>
    </Box>
  );
};
